import React from 'react';
import { Link } from 'react-router-dom';
import PageHead from '../components/PageHead';
import Icon from '../components/Icon';
import { usePageMeta } from '../hooks/usePageMeta';
import { useTocHighlight } from '../hooks/useTocHighlight';

// 8 Oct 2026: every statement here was checked against the app (4.2.7) and the server. Lines claiming data the
// app does NOT collect (sex, country, violence details, aggressor, disability), fingerprint login, security
// audits and "only the app can decrypt" were removed or reworded. See output/sitio/ fact-check page.
// Google Play's Data safety form links to /eliminar-cuenta and must keep matching this page.
const TOC: [string, string][] = [
  ['resumen', 'Resumen'],
  ['introduccion', 'Introducción'],
  ['recopilacion', 'Información'],
  ['uso', 'Uso'],
  ['permisos', 'Permisos'],
  ['ayuda-app', 'Ayuda'],
  ['eliminar-cuenta', 'Tu cuenta'],
  ['consentimiento', 'Consentimiento'],
  ['seguridad', 'Seguridad'],
];

type Sec = { id: string; title: string; body: React.ReactNode };

const SECTIONS: Sec[] = [
  {
    id: 'introduccion',
    title: 'Introducción',
    body: (
      <>
        <p>
          La aplicación Violeta está diseñada para ayudar a mujeres en riesgo o sobrevivientes de violencia basada en
          género. Esta aplicación recopila y procesa información personal de las usuarias, por lo que es importante que
          sepan cómo se utiliza esta información y cómo se protege su privacidad.
        </p>
      </>
    ),
  },
  {
    id: 'recopilacion',
    title: 'Recopilación de información personal',
    body: (
      <>
        <p>La aplicación Violeta recopila la siguiente información personal de las usuarias:</p>
        <ul>
          <li><strong>Información de contacto:</strong> nombre, correo electrónico, número de teléfono.</li>
          <li><strong>Datos de tu perfil (opcionales):</strong> cédula, fecha de nacimiento y estado de residencia.</li>
          <li><strong>Ubicación:</strong> la ubicación actual de la usuaria (solo con su consentimiento expreso).</li>
          <li>
            <strong>Contactos de confianza:</strong> puedes elegirlos de la libreta de tu teléfono (solo con tu permiso).
            Se guardan únicamente en tu teléfono; no se suben a nuestro servidor.
          </li>
          <li>
            <strong>Datos de uso (anónimos):</strong> qué secciones de la app se usan y algunas acciones (por ejemplo,
            abrir el directorio, iniciar Acompáñame o activar el botón de emergencia), junto con un identificador
            aleatorio de la instalación que <strong>no está ligado a tu cuenta</strong>, la versión de la app, el tipo
            de teléfono (Android o iPhone) y, si la indicaste en tu perfil, tu estado de residencia.{' '}
            <strong>No incluye</strong> tu nombre, tus contactos, tus mensajes, tu ubicación ni lo que escribes o
            respondes. Puedes desactivarlo en Ajustes → «Ayudar a mejorar Violeta». Si eliminas tu cuenta, el
            identificador se reemplaza por uno nuevo.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'uso',
    title: 'Uso de la información personal',
    body: (
      <>
        <p>La información personal recopilada por la aplicación Violeta se utiliza para los siguientes fines:</p>
        <ol>
          <li>
            <strong>Ayudarte a avisar a tus contactos de confianza:</strong> la app prepara el mensaje con tu ubicación
            y tú decides si lo envías. Violeta no envía alertas por sí sola y no es un servicio de emergencias: si estás
            en peligro, llama al 911.
          </li>
          <li>
            <strong>Informes para la ONG:</strong> como parte de un proyecto financiado a través de una ONG, necesitamos
            reportar cifras agrupadas (no datos personales de las usuarias) para llevar un registro del uso de la
            aplicación, por ejemplo cuántas personas la usan por estado de residencia y qué secciones se consultan.
          </li>
          <li>
            <strong>Reportes de uso anónimos:</strong> preparar reportes mensuales con números agrupados y anónimos
            sobre el uso de la app, para Tinta Violeta y sus aliados. Los números muy pequeños no se publican.
          </li>
        </ol>
      </>
    ),
  },
  {
    id: 'permisos',
    title: 'Permisos requeridos',
    body: (
      <>
        <p>Para funcionar correctamente, la aplicación Violeta necesita los siguientes permisos:</p>
        <ul>
          <li>
            <strong>Ubicación:</strong> para incluir tu ubicación en el mensaje que envías a tus contactos de
            confianza. Mientras el botón de emergencia o Acompáñame están activos, la app envía tu ubicación a nuestro
            servidor, y con ella se genera el enlace que reciben tus contactos. Acompáñame no envía mensajes por sí
            sola: abre el mensaje para tus contactos y tú tocas «Enviar».
          </li>
          <li><strong>Contactos:</strong> para seleccionar contactos de confianza desde la libreta telefónica.</li>
          <li>
            <strong>Perfil (opcional):</strong> nombre, teléfono, cédula, fecha de nacimiento y estado, para tu cuenta y
            para los reportes agrupados.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'ayuda-app',
    title: 'Ayuda con la app',
    body: (
      <p>
        Si tocas «Necesito ayuda», la app prepara un mensaje de WhatsApp con la versión de la app, el modelo de tu
        teléfono y el último error. <strong>Tú decides si lo envías</strong>; la app no lo envía por ti.
      </p>
    ),
  },
  {
    id: 'eliminar-cuenta',
    title: 'Eliminar tu cuenta y tus datos',
    body: (
      <p>
        Puedes eliminar tu cuenta cuando quieras desde Ajustes → «Eliminar mi cuenta». Los pasos y lo que se borra están
        en <Link to="/eliminar-cuenta">violeta-app.com/eliminar-cuenta</Link>.
      </p>
    ),
  },
  {
    id: 'consentimiento',
    title: 'Consentimiento expreso',
    body: (
      <p>
        Obtendremos el consentimiento expreso de las usuarias antes de recopilar, utilizar o compartir cualquier
        información personal, especialmente en lo que respecta a la ubicación y los contactos.
      </p>
    ),
  },
  {
    id: 'seguridad',
    title: 'Medidas de seguridad',
    body: (
      <>
        <p>Esto es lo que Violeta hace hoy para cuidar tus datos:</p>
        <ul>
          <li>
            <strong>Conexión cifrada:</strong> los datos viajan entre la app y nuestro servidor por una conexión cifrada
            (HTTPS) y se guardan en una base de datos cifrada por su proveedor. No es un cifrado de extremo a extremo:
            nuestro servidor puede leerlos.
          </li>
          <li><strong>Tu cuenta:</strong> entras con tu correo y tu contraseña. Nunca compartas tu contraseña.</li>
          <li>
            <strong>Tus contactos de emergencia:</strong> se guardan solo en tu teléfono y se borran de él si cierras
            sesión o eliminas tu cuenta.
          </li>
          <li>
            <strong>Tu decisión:</strong> puedes desactivar el conteo anónimo de uso en Ajustes y eliminar tu cuenta
            cuando quieras.
          </li>
        </ul>
      </>
    ),
  },
];

const Privacy: React.FC = () => {
  usePageMeta(
    'Política de privacidad · Violeta',
    'Qué datos recopila la app Violeta, para qué se usan y qué medidas de seguridad se aplican.',
  );
  useTocHighlight();

  return (
    <>
      <PageHead
        title="Política de privacidad"
        crumb="Política de privacidad"
        lead="Qué datos usa la app Violeta, para qué y quién puede verlos. Empieza con un resumen en palabras sencillas."
      >
        <p className="meta">
          <span>
            <Icon name="refresh" />
            Actualizada el 8 de octubre de 2026 · app 4.2.7
          </span>
        </p>
      </PageHead>

      <nav className="toc-chips" aria-label="En esta página">
        <div className="container toc-chips__inner">
          <p>En esta página</p>
          <ol data-toc>
            {TOC.map(([id, label], index) => (
              <li key={id}>
                <a href={`#${id}`}>
                  <span>{index === 0 ? '✦' : index}</span>
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </div>
      </nav>

      <div className="legal">
        <div className="container">
          <section className="summary" id="resumen" aria-labelledby="resumen-title">
            <h2 id="resumen-title">Tus datos, en pocas palabras</h2>
            <ul>
              <li>
                <strong>Tú das los permisos.</strong>
                La app te pide permiso para usar tu ubicación y tus contactos. Puedes decir que no y agregarlos a mano.
              </li>
              <li>
                <strong>Tus contactos, en tu teléfono.</strong>
                Tus contactos de emergencia no se suben a nuestro servidor. Si cierras sesión, se borran de ese teléfono.
              </li>
              <li>
                <strong>Ubicación, solo si tú la activas.</strong>
                Durante una alerta o mientras Acompáñame está encendido. Va en el SMS a tus contactos y también al
                servidor de Violeta.
              </li>
              <li>
                <strong>Tus respuestas son tuyas.</strong>
                Lo que respondes en el Violentómetro y en el Cuestionario no sale de tu teléfono.
              </li>
              <li>
                <strong>Puedes borrar tu cuenta cuando quieras.</strong>
                Desde <b>Ajustes</b>, en la app.
              </li>
            </ul>
            <p className="note">Este resumen te ayuda a entender lo principal. No reemplaza el texto completo.</p>
          </section>

          {SECTIONS.map((section, index) => (
            <section className="lsec" key={section.id} aria-labelledby={section.id}>
              <span className="lsec__num" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
              <div className="prose">
                <h2 id={section.id}>
                  {section.title}
                  <a className="anchor" href={`#${section.id}`} aria-label={`Enlace a la sección ${section.title}`}>#</a>
                </h2>
                {section.body}
              </div>
            </section>
          ))}
        </div>
      </div>
    </>
  );
};

export default Privacy;
